import React from 'react';
import { Box, Container, Typography, Link as MuiLink } from '@mui/material';
import { Link } from 'react-router-dom';

const PrivacyPolicy: React.FC = () => {
  return (
    <Box sx={{ backgroundColor: '#FAF5EA', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="md">
        <Box sx={{ mb: 3 }}>
          <MuiLink component={Link} to="/" sx={{ color: '#F78764', fontWeight: 600 }}>
            ← Back to Home
          </MuiLink>
        </Box>

        <Box sx={{ backgroundColor: 'white', borderRadius: 3, p: { xs: 3, md: 5 }, boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700, color: '#2c3e50', mb: 1 }}>
            Privacy Policy
          </Typography>
          <Typography variant="body2" sx={{ color: '#7f8c8d', mb: 4 }}>
            Last Updated: November 29, 2025
          </Typography>

          <Section title="Introduction">
            <Typography paragraph>
              Snackd, LLC ("we," "our," or "us") operates the Snackd mobile application and website (collectively, the "Service"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service.
            </Typography>
            <Typography paragraph>
              By using Snackd, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our Service.
            </Typography>
            <Typography paragraph>
              <strong>Contact Information:</strong><br />
              Email: support@snackdapp.com<br />
              Mailing Address: Snackd, LLC c/o Republic Registered Agent LLC 36 Russ St, 3rd Floor Hartford, CT 06106 United States
            </Typography>
          </Section>

          <Section title="1. Information We Collect">
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>1.1 Information You Provide Directly</Typography>

            <Typography paragraph><strong>Account Information:</strong></Typography>
            <ul>
              <li>Name</li>
              <li>Email address</li>
              <li>Password (encrypted)</li>
              <li>Profile preferences</li>
            </ul>

            <Typography paragraph><strong>Family and Household Information:</strong></Typography>
            <ul>
              <li>Number of family members</li>
              <li>Names of family members (optional)</li>
              <li>Age ranges of family members</li>
              <li>Dietary preferences and restrictions (vegetarian, vegan, allergies, medical conditions)</li>
              <li>Meal schedules (breakfast, lunch, dinner preferences)</li>
              <li>Budget preferences</li>
              <li>Meal preparation time preferences</li>
            </ul>

            <Typography paragraph><strong>Meal Planning Data:</strong></Typography>
            <ul>
              <li>Recipe interactions (favorites, dislikes, ratings)</li>
              <li>Meal plan history</li>
              <li>Shopping lists</li>
              <li>Pantry inventory (if provided)</li>
              <li>Cooking completion data</li>
            </ul>

            <Typography paragraph><strong>Payment Information:</strong></Typography>
            <Typography paragraph>
              Payment method details are processed and stored by our payment processor, Stripe. We do not store complete credit card information on our servers.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, mt: 3 }}>1.2 Information Collected Automatically</Typography>
            <Typography paragraph><strong>Usage Information:</strong></Typography>
            <ul>
              <li>Device information (type, operating system, unique device identifiers)</li>
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>App usage patterns and interactions</li>
              <li>Pages viewed and features used</li>
              <li>Date and time of access</li>
              <li>Crash reports and error logs</li>
            </ul>

            <Typography paragraph><strong>Location Information:</strong></Typography>
            <Typography paragraph>
              We do not collect precise geolocation data. General location may be inferred from IP address for service optimization.
            </Typography>
          </Section>

          <Section title="2. How We Use Your Information">
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>2.1 Provide and Improve Our Service</Typography>
            <ul>
              <li>Generate personalized meal plans using AI technology</li>
              <li>Create shopping lists based on your meal plans</li>
              <li>Recommend recipes that match your dietary preferences and restrictions</li>
              <li>Track your meal planning history and preferences</li>
              <li>Provide customer support and respond to your requests</li>
              <li>Send transactional emails (password resets, account notifications)</li>
            </ul>

            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, mt: 3 }}>2.2 AI-Powered Features</Typography>
            <ul>
              <li>We use Anthropic's Claude AI technology to generate meal plans, recipes, and recommendations</li>
              <li>Your dietary preferences, restrictions, and family information are sent to Anthropic's API to create personalized content</li>
              <li>Anthropic processes this information according to their own privacy policy and data usage terms</li>
              <li>We do not train AI models on your personal data</li>
            </ul>

            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, mt: 3 }}>2.3 Marketing Communications (With Your Consent)</Typography>
            <Typography paragraph>
              Send promotional emails about new features, special offers, or content we think you might find interesting. You can opt out of marketing emails at any time using the unsubscribe link in any email.
            </Typography>
          </Section>

          <Section title="3. How We Share Your Information">
            <Typography paragraph sx={{ fontWeight: 600 }}>
              We do not sell, rent, or trade your personal information.
            </Typography>
            <Typography paragraph>We share information only with the following service providers:</Typography>

            <Typography paragraph><strong>Anthropic (Meal Plan Generation):</strong></Typography>
            <ul>
              <li>Purpose: Generate AI-powered meal plans and recipes</li>
              <li>Data Shared: Dietary preferences, restrictions, family size, meal preferences</li>
              <li>Privacy Policy: <MuiLink href="https://www.anthropic.com/privacy" target="_blank" rel="noopener">anthropic.com/privacy</MuiLink></li>
            </ul>

            <Typography paragraph><strong>Stripe (Payment Processing):</strong></Typography>
            <ul>
              <li>Purpose: Process subscription payments</li>
              <li>Data Shared: Email, payment method details</li>
              <li>Privacy Policy: <MuiLink href="https://stripe.com/privacy" target="_blank" rel="noopener">stripe.com/privacy</MuiLink></li>
            </ul>

            <Typography paragraph><strong>SMTP2GO (Transactional Emails):</strong></Typography>
            <ul>
              <li>Purpose: Send password resets, account verification, and service notifications</li>
              <li>Data Shared: Email address, name</li>
              <li>Privacy Policy: <MuiLink href="https://www.smtp2go.com/privacy" target="_blank" rel="noopener">smtp2go.com/privacy</MuiLink></li>
            </ul>

            <Typography paragraph><strong>DigitalOcean (Cloud Hosting):</strong></Typography>
            <ul>
              <li>Purpose: Host our servers and databases</li>
              <li>Data Shared: All data stored in our application</li>
              <li>Privacy Policy: <MuiLink href="https://www.digitalocean.com/legal/privacy-policy" target="_blank" rel="noopener">digitalocean.com/legal/privacy-policy</MuiLink></li>
            </ul>

            <Typography paragraph><strong>Legal Requirements:</strong></Typography>
            <Typography paragraph>
              We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., court orders, subpoenas, or government agencies).
            </Typography>
          </Section>

          <Section title="4. Data Security">
            <Typography paragraph>
              We implement appropriate technical and organizational security measures to protect your personal information:
            </Typography>
            <ul>
              <li><strong>Encryption:</strong> All data transmitted between your device and our servers is encrypted using SSL/TLS</li>
              <li><strong>Password Security:</strong> Passwords are hashed and encrypted using industry-standard algorithms</li>
              <li><strong>Access Controls:</strong> Limited employee access to personal data on a need-to-know basis</li>
              <li><strong>Regular Security Audits:</strong> We regularly review our security practices</li>
              <li><strong>Secure Infrastructure:</strong> Our servers are hosted on secure cloud infrastructure</li>
            </ul>
            <Typography paragraph>
              However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect your personal information, we cannot guarantee absolute security.
            </Typography>
          </Section>

          <Section title="5. Data Retention">
            <Typography paragraph>
              We retain your personal information for as long as necessary to provide you with our Service and fulfill the purposes outlined in this Privacy Policy.
            </Typography>
            <ul>
              <li><strong>Account Data:</strong> Retained while your account is active</li>
              <li><strong>Deleted Accounts:</strong> Data is permanently deleted within 30 days of account deletion request</li>
              <li><strong>Payment Records:</strong> Retained for 7 years for tax and legal compliance purposes</li>
              <li><strong>Analytics Data:</strong> Anonymized usage data may be retained indefinitely for service improvement</li>
            </ul>
            <Typography paragraph>
              You can request deletion of your account and associated data at any time through your account settings or by contacting support@snackdapp.com.
            </Typography>
          </Section>

          <Section title="6. Your Privacy Rights">
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>6.1 Access and Portability</Typography>
            <Typography paragraph>
              You have the right to access the personal information we hold about you and request a copy of your data in a portable format (JSON or CSV).
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>6.2 Correction and Updates</Typography>
            <Typography paragraph>
              You can update your account information, family member details, and preferences at any time through the app settings.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>6.3 Deletion</Typography>
            <Typography paragraph>
              You have the right to request deletion of your account and personal information. To delete your account, go to Account Settings → Delete Account in the app, or email support@snackdapp.com with your deletion request.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>6.4 Marketing Opt-Out</Typography>
            <Typography paragraph>
              You can opt out of promotional emails by clicking the "unsubscribe" link in any marketing email, adjusting your email preferences in account settings, or emailing support@snackdapp.com.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>6.5 California Privacy Rights (CCPA)</Typography>
            <Typography paragraph>
              If you are a California resident, you have additional rights including the right to know what personal information is collected and used, the right to delete personal information, and the right to opt-out of the sale of personal information. Note: We do not sell personal information.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>6.6 European Privacy Rights (GDPR)</Typography>
            <Typography paragraph>
              If you are located in the European Economic Area, you have additional rights under the GDPR including the right to access, rectification, erasure, restrict processing, data portability, and the right to object to processing.
            </Typography>
          </Section>

          <Section title="7. Children's Privacy">
            <Typography paragraph>
              Snackd is not intended for use by children under the age of 13. We do not knowingly collect personal information from children under 13.
            </Typography>
            <Typography paragraph>
              While our Service helps families plan meals (which may include children), account holders must be 18 years or older. Any information about family members (including children) is provided by the adult account holder and is used solely for meal planning purposes.
            </Typography>
          </Section>

          <Section title="8. International Data Transfers">
            <Typography paragraph>
              Your information may be transferred to and processed in countries other than your country of residence, including the United States, where our servers are located.
            </Typography>
            <Typography paragraph>
              By using our Service, you consent to the transfer of your information to the United States and other countries where we or our service providers operate.
            </Typography>
          </Section>

          <Section title="9. Cookies and Tracking Technologies">
            <Typography paragraph><strong>What We Use:</strong></Typography>
            <ul>
              <li><strong>Essential Cookies:</strong> Required for basic Service functionality (authentication, security)</li>
              <li><strong>Analytics:</strong> To understand how users interact with our Service (anonymous usage data)</li>
              <li><strong>Preferences:</strong> To remember your settings and preferences</li>
            </ul>
            <Typography paragraph>
              We do not use third-party advertising cookies or tracking pixels. You can control cookies through your browser settings.
            </Typography>
          </Section>

          <Section title="10. Changes to This Privacy Policy">
            <Typography paragraph>
              We may update this Privacy Policy from time to time. We will post the updated Privacy Policy on this page, update the "Last Updated" date, and for material changes, notify you via email or prominent notice in the app.
            </Typography>
          </Section>

          <Section title="11. Contact Us">
            <Typography paragraph>
              If you have any questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us:
            </Typography>
            <Typography paragraph>
              <strong>Email:</strong> support@snackdapp.com
            </Typography>
            <Typography paragraph>
              <strong>Mail:</strong><br />
              Snackd, LLC<br />
              c/o Republic Registered Agent LLC<br />
              36 Russ St, 3rd Floor<br />
              Hartford, CT 06106<br />
              United States
            </Typography>
            <Typography paragraph>
              <strong>Response Time:</strong> We will respond to privacy-related inquiries within 30 days.
            </Typography>
          </Section>

          <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e0e0e0' }}>
            <Typography variant="body2" sx={{ color: '#7f8c8d', textAlign: 'center' }}>
              © 2025 Snackd, LLC. All rights reserved.
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h5" sx={{ fontWeight: 600, color: '#2c3e50', mb: 2 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

export default PrivacyPolicy;
